// @ts-check
// Panel del rediseño (Fase 4a): la superficie que agrupa una parte de una
// pantalla (los paneles del HUD, la paleta, el enunciado…). Lleva su título
// y, si el plan de la Fase 3 lo pide, una etiqueta corta encima.
//
// Regla de la guía: NUNCA un panel dentro de otro. Dentro de un panel solo van
// filas, listas, pozos (`.ui-pozo`) y avisos (`.ui-aviso`).
import { createSafeElement } from '../../security/safe-dom.js';
import './panel.css';

/** Para dar a cada título un id único con el que nombrar su panel. */
let contadorPaneles = 0;

/**
 * @typedef {Object} OpcionesPanel
 * @property {string} titulo - Texto del encabezado del panel.
 * @property {2 | 3 | 4} [nivel] - Nivel del encabezado. Por defecto 2.
 * @property {string} [etiqueta] - Etiqueta corta sobre el título (p. ej.
 *   «Flujo · Editor de diagramas»). Es decorativa si repite contexto; no la
 *   uses para información que no esté en otra parte.
 * @property {boolean} [tituloComoEtiqueta] - El título se pinta con el estilo
 *   de etiqueta (así son «Paleta de bloques», «Progreso» o «Autocorregir
 *   ejercicio» en la maqueta): sigue siendo un encabezado de verdad.
 * @property {boolean} [destacado] - Borde de acento (el bloque en curso de
 *   Progreso, el panel que pide atención).
 * @property {'section' | 'div' | 'aside'} [elemento] - Por defecto `section`.
 * @property {string} [clase] - Clase(s) extra para la página que lo monta.
 */

/**
 * Crea un panel con su cabecera. Devuelve el panel, la cabecera (para añadir
 * acciones a la derecha del título) y el cuerpo (donde va el contenido).
 * @param {OpcionesPanel} opciones
 * @returns {{ elemento: HTMLElement, cabecera: HTMLElement, titulo: HTMLElement, cuerpo: HTMLElement }}
 */
export function crearPanel({
  titulo,
  nivel = 2,
  etiqueta,
  tituloComoEtiqueta = false,
  destacado = false,
  elemento = 'section',
  clase,
}) {
  const clases = ['ui-panel'];
  if (destacado) clases.push('ui-panel-destacado');
  if (clase) clases.push(clase);
  const panel = createSafeElement(elemento, { className: clases.join(' ') });

  const cabecera = createSafeElement('div', { className: 'ui-panel-cabecera' });
  const bloqueTitulo = createSafeElement('div', { className: 'ui-panel-titulos' });
  if (etiqueta) {
    bloqueTitulo.append(createSafeElement('p', { className: 'ui-etiqueta', text: etiqueta }));
  }
  const encabezado = createSafeElement(`h${nivel}`, {
    className: tituloComoEtiqueta ? 'ui-panel-titulo ui-etiqueta' : 'ui-panel-titulo',
    text: titulo,
  });
  bloqueTitulo.append(encabezado);
  cabecera.append(bloqueTitulo);

  const cuerpo = createSafeElement('div', { className: 'ui-panel-cuerpo' });
  panel.append(cabecera, cuerpo);

  // Una región con nombre: el panel se anuncia por su título.
  if (elemento === 'section') {
    contadorPaneles += 1;
    encabezado.id = `ui-panel-titulo-${contadorPaneles}`;
    panel.setAttribute('aria-labelledby', encabezado.id);
  }

  return { elemento: panel, cabecera, titulo: encabezado, cuerpo };
}
